import * as React from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { RootState } from '../../reducers'
import './NavbarV2.scss'
import { bindActionCreators, Dispatch } from 'redux'
import * as actions from '../../actions/generalActions'
import { withRouter } from 'react-router'
import { Link, Route } from 'react-router-dom'
import AdminNav from '../Admin/AdminNav'
import classNames from 'classnames'
import UserGlyph from './UserGlyph'
import { ReactComponent as LogOut } from '../../assets/icons/sign-out.svg'
import { useLocale } from '../../hooks/useLocale'
import { LOCALE_META, SupportedLocale } from '../../i18n/config'
import NotificationBell from '../NotificationBell'
import {
    SECTIONS,
    MODULE_I18N_KEY,
    MODULE_ICON,
    MODULE_URL_OVERRIDE,
    canSeeModule as canSeeModuleFn,
    isModuleEnabled as isModuleEnabledFn,
} from './navModel'

/**
 * Function component wrapper for the locale switcher — NavbarV2 is a class
 * component so it can't call `useLocale()` directly. Renders the same trigger
 * + dropdown pattern as the mobile nav's footer locale picker. Self-contained
 * state means it doesn't need anything injected via Redux.
 */
const NavLocaleSwitcher: React.FC = () => {
    const { locale, setLocale, activeLocales } = useLocale()
    const [open, setOpen] = React.useState(false)

    const langOptions = (activeLocales && activeLocales.length ? activeLocales : Object.keys(LOCALE_META)) as SupportedLocale[]
    if (langOptions.length <= 1) return null

    const currentMeta = LOCALE_META[locale]
    return (
        <div className={`nav2__lang ${open ? 'is-open' : ''}`}>
            {open && (
                <ul className="nav2__lang-options" onClick={(e) => e.stopPropagation()}>
                    {langOptions.map((code) => {
                        const meta = LOCALE_META[code]
                        const isActive = code === locale
                        return (
                            <li
                                key={code}
                                className={`nav2__lang-option ${isActive ? 'is-active' : ''}`}
                                onClick={() => { setOpen(false); if (!isActive) setLocale(code) }}
                            >
                                <span className="nav2__lang-option-native">{meta?.native ?? code}</span>
                                <span className="nav2__lang-option-code">{code}</span>
                            </li>
                        )
                    })}
                </ul>
            )}
            <button
                type="button"
                className="nav2__lang-trigger"
                onClick={() => setOpen(v => !v)}
                aria-expanded={open}
            >
                <span className="nav2__lang-trigger-label">
                    <FormattedMessage id="user.menu.language" defaultMessage="Language" />
                </span>
                <span className="nav2__lang-trigger-value">
                    {currentMeta?.native ?? locale}
                    <span className="nav2__lang-trigger-caret">{open ? '▾' : '▸'}</span>
                </span>
            </button>
        </div>
    )
}

interface Props {
    step: number
    actions: any
    history: any
    location: any
    intl: any
    isNavOpen: boolean
    isAdminPanelOpen: boolean
    permissions: any
    user: any
}

interface State {
    showLogoutConfirm: boolean
    // Per-group collapse state for the Sales-context section labels. Keyed by
    // section id; absent / false = expanded. Admin context groups are handled
    // by AdminNav, which owns its own persisted collapse state.
    collapsedGroups: Record<string, boolean>
}

/**
 * Live viewport read at handler / render time. Replaces the stale module-loaded
 * `isMobile` constant from constants.tsx which was evaluated once at app boot
 * and never updated on resize / rotate / DevTools mobile emulation — leaving
 * the admin drawer stuck open on /admin routes.
 *
 * Class component, so we can't use the `useIsMobile` hook here; this is the
 * synchronous equivalent. See hooks/useIsMobile.ts for the function-component
 * version used by Dashboard.tsx.
 */
const checkIsMobile = (): boolean => typeof window !== 'undefined' && window.innerWidth < 576

// Nav data model — sections, icons, labels, URLs, permission gates — lives in
// ./navModel, shared with the mobile nav so the two surfaces can't drift.

class NavbarV2 extends React.Component<Props, State> {
    constructor(props: any) {
        super(props)
        this.state = { showLogoutConfirm: false, collapsedGroups: {} }
    }

    // Toggle a Sales-context section group open/closed. Chevron rotates and the
    // group's item list unmounts when collapsed (mirrors AdminNav's group
    // behaviour and the prototype's click-to-collapse group labels).
    toggleGroup = (id: string) => {
        this.setState(prev => ({
            collapsedGroups: { ...prev.collapsedGroups, [id]: !prev.collapsedGroups[id] },
        }))
    }

    isModuleEnabled(module: string): boolean {
        return isModuleEnabledFn(module, (this.props as any)?.companyProfile)
    }

    canSeeModule(moduleKey: string): boolean {
        return canSeeModuleFn(moduleKey, this.props.permissions, (this.props as any)?.companyProfile)
    }

    render() {
        const fm = (id: string, defaultMessage: string) =>
            this.props.intl.formatMessage({ id, defaultMessage })

        const collapsed = this.props.isNavOpen

        // Evaluated at click time so the answer is current — the user may have
        // resized or rotated since this component last rendered. The setter is
        // a no-op on desktop (where the nav is always-visible).
        const onClickCloseNavbarForMobile = () => {
            if (checkIsMobile()) this.props?.actions?.setNavBarOpen(true)
        }

        // Render-time viewport read so the mobile footer + admin-link close
        // handler both honour the current viewport, not a stale boot-time read.
        const isMobile = checkIsMobile()

        const pathName = this.props.history.location.pathname.split('/')
        const activeModule = pathName[2]
        const currentPath = this.props.history.location.pathname
        const isSubActive = (to: string) => (currentPath === to ? 'sub-active' : '')

        const renderModuleRow = (moduleKey: string) => {
            if (!this.canSeeModule(moduleKey)) return null
            const labelKey = MODULE_I18N_KEY[moduleKey]
            const icon = MODULE_ICON[moduleKey]
            const moduleUrl = MODULE_URL_OVERRIDE[moduleKey] || `/dashboard/${moduleKey}`
            const isActive = activeModule === moduleKey || currentPath === moduleUrl

            const showSubMenu =
                isActive &&
                !collapsed &&
                Object.keys(this.props?.permissions || {}).length !== 0 &&
                moduleKey !== 'measures' &&
                moduleKey !== 'contact' &&
                moduleKey !== 'warehouse' &&
                moduleKey !== 'service' &&
                moduleKey !== 'analytics'

            return (
                <li
                    key={moduleKey}
                    className={classNames('nav-row', { 'nav-row--active': isActive })}
                >
                    <Link
                        to={moduleUrl}
                        onClick={onClickCloseNavbarForMobile}
                        className="nav-row__link"
                        data-tooltip={fm(labelKey, moduleKey)}
                    >
                        <span className="nav-row__icon">
                            <img src={icon} alt="" />
                        </span>
                        <span className="nav-row__label">
                            <FormattedMessage id={labelKey} defaultMessage={moduleKey} />
                        </span>
                    </Link>

                    {showSubMenu && (
                        <ul className="nav-submenu">
                            {this.props?.permissions?.fullPrivileges &&
                                moduleKey !== 'transport' &&
                                moduleKey !== 'installation' &&
                                moduleKey !== 'production' && (
                                    <li>
                                        <Link
                                            onClick={onClickCloseNavbarForMobile}
                                            to={`/dashboard/${moduleKey}`}
                                            className={isSubActive(`/dashboard/${moduleKey}`)}
                                        >
                                            {moduleKey === 'agreements' ? (
                                                <FormattedMessage id="nav.sub.agreements" defaultMessage="Agreements" />
                                            ) : (
                                                <FormattedMessage id="nav.sub.all" defaultMessage="View all" />
                                            )}
                                        </Link>
                                    </li>
                                )}
                            {moduleKey === 'offers' && (
                                <li>
                                    <Link
                                        onClick={onClickCloseNavbarForMobile}
                                        to={`/dashboard/${moduleKey}/canceled/products`}
                                        className={isSubActive(`/dashboard/${moduleKey}/canceled/products`)}
                                    >
                                        <FormattedMessage id="nav.sub.canceledProducts" defaultMessage="Canceled products" />
                                    </Link>
                                </li>
                            )}
                            {moduleKey === 'agreements' && (
                                <li>
                                    <Link
                                        onClick={onClickCloseNavbarForMobile}
                                        to={`/dashboard/${moduleKey}/project/calendar`}
                                        className={isSubActive(`/dashboard/${moduleKey}/project/calendar`)}
                                    >
                                        <FormattedMessage id="nav.sub.agreementCalendar" defaultMessage="Calendar" />
                                    </Link>
                                </li>
                            )}
                            {moduleKey === 'production' &&
                                (this.props?.permissions?.fullPrivileges || this.props?.permissions[moduleKey]) && (
                                    <li>
                                        <Link
                                            onClick={onClickCloseNavbarForMobile}
                                            to={`/dashboard/${moduleKey}/board`}
                                            className={isSubActive(`/dashboard/${moduleKey}/board`)}
                                        >
                                            <FormattedMessage id="nav.sub.productionInformation" defaultMessage="Production info" />
                                        </Link>
                                    </li>
                                )}
                            {moduleKey === 'production' && this.props?.permissions?.fullPrivileges && (
                                <>
                                    <li>
                                        <Link onClick={onClickCloseNavbarForMobile} to={`/dashboard/${moduleKey}/products`} className={isSubActive(`/dashboard/${moduleKey}/products`)}>
                                            <FormattedMessage id="nav.sub.productionProducts" defaultMessage="Ordered products" />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link onClick={onClickCloseNavbarForMobile} to={`/dashboard/production/calendar`} className={isSubActive(`/dashboard/production/calendar`)}>
                                            <FormattedMessage id="nav.sub.productionCalendar" defaultMessage="Calendar" />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link onClick={onClickCloseNavbarForMobile} to={`/dashboard/production/archived-responsibilities`} className={isSubActive(`/dashboard/production/archived-responsibilities`)}>
                                            <FormattedMessage id="nav.sub.archivedProcesses" defaultMessage="Process archive" />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link onClick={onClickCloseNavbarForMobile} to={`/dashboard/production/archive`} className={isSubActive(`/dashboard/production/archive`)}>
                                            <FormattedMessage id="nav.sub.productionArchive" defaultMessage="Production archive" />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link onClick={onClickCloseNavbarForMobile} to={`/dashboard/production/archived-products`} className={isSubActive(`/dashboard/production/archived-products`)}>
                                            <FormattedMessage id="nav.sub.archivedProducts" defaultMessage="Product archive" />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link onClick={onClickCloseNavbarForMobile} to={`/dashboard/production/returned-processes`} className={isSubActive(`/dashboard/production/returned-processes`)}>
                                            <FormattedMessage id="nav.sub.returnedProcesses" defaultMessage="Return history" />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link onClick={onClickCloseNavbarForMobile} to={`/dashboard/production/passive-products`} className={isSubActive(`/dashboard/production/passive-products`)}>
                                            <FormattedMessage id="nav.sub.passiveProducts" defaultMessage="Passive products" />
                                        </Link>
                                    </li>
                                </>
                            )}
                            {moduleKey === 'impression' && (
                                <>
                                    <li>
                                        <Link onClick={onClickCloseNavbarForMobile} to={`/dashboard/${moduleKey}/1`} className={isSubActive(`/dashboard/${moduleKey}/1`)}>
                                            <FormattedMessage id="nav.sub.impressionPending" defaultMessage="Pending projects" />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link onClick={onClickCloseNavbarForMobile} to={`/dashboard/${moduleKey}/2`} className={isSubActive(`/dashboard/${moduleKey}/2`)}>
                                            <FormattedMessage id="nav.sub.impressionAll" defaultMessage="All impressions" />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link onClick={onClickCloseNavbarForMobile} to={`/dashboard/${moduleKey}/create`} className={isSubActive(`/dashboard/${moduleKey}/create`)}>
                                            <FormattedMessage id="nav.sub.impressionCreate" defaultMessage="Create new" />
                                        </Link>
                                    </li>
                                </>
                            )}
                        </ul>
                    )}
                </li>
            )
        }

        const username = this.props.user?.username || localStorage.getItem('username') || ''

        // Single toggle handler — same semantics as the old Header hamburger
        // (which is no longer rendered on desktop in the Option C layout).
        const handleToggle = () => {
            this.props.actions.setNavBarOpen(!this.props.isNavOpen)
        }

        return (
            <aside className={classNames('nav2', { 'nav2--collapsed': collapsed })}>
                {/* ── Header row: user pill (left) + hamburger/X toggle (right) ───
                    On expanded width: avatar + name + role on the left, X on the
                    right (justify-content: space-between via SCSS). On collapsed
                    width: pill hidden, the collapse chevron centred alone in
                    the column. Mirrors the MobileNav drawer header so mobile +
                    desktop share the same visual pattern. */}
                <div className="nav2__header">
                    {/* Pill + bell stay mounted so they fade + collapse (max-width
                        → 0) when the rail closes, instead of popping out. See
                        &__header-user / .bell collapsed rules in SCSS. */}
                    <div className="nav2__header-user" aria-hidden={collapsed}>
                        <span className="nav2__header-avatar"><UserGlyph /></span>
                        <div className="nav2__header-meta">
                            <div className="nav2__header-name">{username}</div>
                            <div className="nav2__header-role">
                                {this.props.permissions?.superAdmin
                                    ? <FormattedMessage id="admin.role.superAdmin" defaultMessage="Super admin" />
                                    : this.props.permissions?.fullPrivileges
                                        ? <FormattedMessage id="admin.role.admin" defaultMessage="Admin" />
                                        : <FormattedMessage id="admin.role.user" defaultMessage="User" />}
                            </div>
                        </div>
                    </div>
                    <NotificationBell />
                    <button
                        type="button"
                        className="nav2__toggle"
                        onClick={handleToggle}
                        aria-label={this.props.intl.formatMessage(
                            collapsed
                                ? { id: 'nav.menu.expand', defaultMessage: 'Expand menu' }
                                : { id: 'nav.menu.collapse', defaultMessage: 'Collapse menu' }
                        )}
                        aria-expanded={!collapsed}
                    >
                        {/* Single chevron — ‹ collapses to the icon rail, › expands back.
                            Replaces the old hamburger/X so the control reads as a
                            width toggle, matching the desktop spec. */}
                        {collapsed ? (
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 5l7 7-7 7" />
                            </svg>
                        ) : (
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 5l-7 7 7 7" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* ── Module list ────────────────────────── */}
                <Route
                    path="/dashboard"
                    render={() => (
                        <nav className="nav2__scroll">
                            {SECTIONS.map(section => {
                                const visibleModules = section.modules.filter(m => this.canSeeModule(m))
                                if (visibleModules.length === 0) return null
                                // Collapse only applies when expanded — in the rail every
                                // group's icons stay visible (there are no labels to click).
                                const isGroupCollapsed = !collapsed && !!this.state.collapsedGroups[section.id]
                                return (
                                    <div key={section.id} className="nav2__section">
                                        {/* Always rendered so it can fade + collapse height
                                            (not unmount) when the rail closes — see
                                            &__section-label in SCSS. */}
                                        <button
                                            type="button"
                                            className={classNames('nav2__section-label', { 'is-collapsed': isGroupCollapsed })}
                                            onClick={() => this.toggleGroup(section.id)}
                                            aria-expanded={!isGroupCollapsed}
                                            tabIndex={collapsed ? -1 : 0}
                                        >
                                            <FormattedMessage id={section.labelId} defaultMessage={section.defaultLabel} />
                                            <span className="nav2__section-chev" aria-hidden="true">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="6 9 12 15 18 9" />
                                                </svg>
                                            </span>
                                        </button>
                                        {!isGroupCollapsed && (
                                            <ul className="nav2__list">
                                                {visibleModules.map(m => renderModuleRow(m))}
                                            </ul>
                                        )}
                                    </div>
                                )
                            })}
                        </nav>
                    )}
                />

                <Route
                    path="/admin"
                    render={() => (
                        <div className="nav2__admin">
                            <AdminNav
                                currentPath={this.props?.location?.pathname || ''}
                                onLinkClick={isMobile ? onClickCloseNavbarForMobile : undefined}
                                collapsed={collapsed}
                            />
                        </div>
                    )}
                />

                {/* ── Footer ─────────────────────────────
                    Only renders when the sidebar is expanded — collapsed
                    column has no horizontal room for buttons. Mobile uses
                    its own MobileNav (so this block no longer needs
                    an isMobile gate; isMobile branch in Dashboard.tsx
                    routes away from NavbarV2 entirely). */}
                {!collapsed && (
                    <div className="nav2__footer">
                        <NavLocaleSwitcher />

                        {currentPath.startsWith('/admin') ? (
                            <button
                                className="nav2__footer-btn nav2__footer-btn--ghost"
                                onClick={() => {
                                    this.props.actions.openAdminPanel(false)
                                    this.props.history.push('/dashboard/contact')
                                }}
                            >
                                <FormattedMessage id="nav.user.backToSystem" defaultMessage="Back to system" />
                            </button>
                        ) : (
                            this.props.permissions?.fullPrivileges && (
                                <button
                                    className="nav2__footer-btn nav2__footer-btn--ghost"
                                    onClick={() => {
                                        this.props.actions.openAdminPanel(true)
                                        this.props.history.push('/admin')
                                    }}
                                >
                                    <FormattedMessage id="user.menu.settings" defaultMessage="Settings" />
                                </button>
                            )
                        )}
                        <button
                            className="nav2__footer-btn nav2__footer-btn--danger"
                            onClick={() => this.setState({ showLogoutConfirm: true })}
                        >
                            <LogOut style={{ width: 14, height: 14 }} />
                            <FormattedMessage id="user.menu.logout" defaultMessage="Log out" />
                        </button>
                    </div>
                )}

                {this.state.showLogoutConfirm && (
                    <div className="nav2__logout-overlay" onClick={() => this.setState({ showLogoutConfirm: false })}>
                        <div className="nav2__logout-modal" onClick={e => e.stopPropagation()}>
                            <LogOut style={{ width: 24, height: 24, color: '#ef4444' }} />
                            <h3><FormattedMessage id="user.menu.logoutConfirmTitle" defaultMessage="Log out?" /></h3>
                            <p><FormattedMessage id="user.menu.logoutConfirmMessage" defaultMessage="Your session will end and you'll be redirected to the login page." /></p>
                            <div className="nav2__logout-actions">
                                <button onClick={() => this.setState({ showLogoutConfirm: false })}>{fm('common.confirm.cancel', 'Cancel')}</button>
                                <button
                                    className="nav2__logout-confirm"
                                    onClick={() => {
                                        this.setState({ showLogoutConfirm: false })
                                        localStorage.removeItem('mikaBearerToken')
                                        localStorage.removeItem('username')
                                        localStorage.removeItem('mikaUserId')
                                        localStorage.removeItem('userPermissions')
                                        localStorage.removeItem('mikaNavbarToggled')
                                        this.props.actions.isLogin(false)
                                        this.props.actions.isAuthenticated(false)
                                        this.props.history.push({ pathname: '/login' })
                                    }}
                                >
                                    {fm('user.menu.logoutConfirmYes', 'Yes, log out')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </aside>
        )
    }
}

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        step: state.app.step,
        user: state.user,
    }
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(NavbarV2)))
